import { AUTH_ROUTES } from 'src/shared/constants/routes';

/** Prefix Amplify uses for every Cognito token cookie it writes. */
const COGNITO_COOKIE_PREFIX = 'CognitoIdentityServiceProvider';

/**
 * Local session teardown + the hard navigation to login after a forced sign-out.
 *
 * WHY THIS IS AN OBJECT WITH METHODS, not bare exported functions:
 *
 * jsdom installs `window.location` as a non-configurable accessor and every
 * `Location` member as a non-configurable own property, so the redirect cannot be
 * observed in place — `Object.defineProperty` throws "Cannot redefine property:
 * location", `delete` silently returns false, `jest.spyOn`/`jest.replaceProperty`
 * throw "not declared configurable", and a `pathname` write is routed into jsdom's
 * unimplemented navigation and discarded. So callers need a seam.
 *
 * A bare exported function (the `redirectToHome` shape in src/helper/maintenanceMode.ts)
 * is this repo's usual seam, and `jest.mock` on it works for consumers a test file
 * imports itself. It does NOT work for `src/helper/api.ts`: the MSW handlers under
 * tests/mocks/api/ import `getBaseUrl` from it, and tests/unit/setupTests.ts imports
 * that server on its first line — so `api.ts` is fully evaluated during setup, before
 * any test file's `jest.mock` registers, and it keeps its binding to the real module.
 * (Verified: the mock registers and `jest.isMockFunction` is true in the test, yet the
 * real navigation still runs.) `jest.resetModules` would fix the binding but fork the
 * module graph, handing the test a different `userProfileBootGuard` than its assertions
 * read. A `setupFiles`-registered mock would also work; the object shape was chosen so
 * the seam is self-contained and needs no global test wiring. The cost is a mutable,
 * unfreezable export — it cannot be `Object.freeze`d without breaking the `jest.spyOn`
 * it exists for. Anything able to reassign these methods already has script execution
 * in this origin and can read the (non-HttpOnly) Cognito cookies directly, so this is
 * not the weakest link — but it is a deliberate trade, recorded rather than implied.
 */
export const sessionRedirect = {
  /**
   * Best-effort local credential wipe for the path where `signOut()` REJECTED.
   *
   * Load-bearing for APX-556's "the session is terminated locally" — without it that
   * claim is false. Amplify stores Cognito tokens in cookies (`CookieStorage`, set in
   * AuthContext.configureAmplify), and the ONLY thing that clears them is `signOut()`.
   * Amplify rejects *before* clearing: `assertTokenProviderConfig` throws first, and
   * `clientSignOut`/`globalSignOut` swallow their own network errors — so the cases
   * that actually reject are exactly the ones that skip token clearing. Left alone, the
   * user reaches /login still holding an access token the server verifies
   * (src/server/auth/session.ts), i.e. navigated away from rather than signed out.
   *
   * Attributes must match what configureAmplify sets (domain = hostname, path = /,
   * sameSite strict, secure on https) or the browser treats it as a different cookie
   * and the expiry silently no-ops.
   */
  clearCognitoCookies(): void {
    /* istanbul ignore if -- SSR/Node has no document */
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    try {
      for (const rawCookie of document.cookie.split(';')) {
        const name = rawCookie.split('=')[0]?.trim();
        if (!name?.startsWith(COGNITO_COOKIE_PREFIX)) continue;
        document.cookie = `${name}=; Path=/; Domain=${window.location.hostname}; Max-Age=0; SameSite=Strict${
          window.location.protocol === 'https:' ? '; Secure' : ''
        }`;
      }
    } catch (_cookieError) {
      // Best-effort: a blocked cookie jar must not stop the redirect below.
    }
  },

  /**
   * Hard navigation to login. HARD deliberately: the Redux store and every cached list
   * built for the now-dead session are thrown away rather than carried into the login
   * screen. It is also what makes the sign-out visible — without it a 401 whose
   * `signOut()` rejected leaves the user on an authenticated-looking page whose every
   * action re-fails (APX-556).
   *
   * The `typeof window` guard lives HERE rather than at each call site: this is the one
   * thing the seam exists to encapsulate, and `src/helper/api.ts` is reachable from
   * server-side paths.
   */
  /* istanbul ignore next -- single delegation to an unmockable browser API */
  toLogin(): void {
    if (typeof window === 'undefined') return;
    window.location.pathname = AUTH_ROUTES.LOGIN;
  },
};
